
import * as THREE from "../../../libs/three.js/build/three.module.js";

// QA-only visualisation for "Areal og volumen".
//
// ── Measurement TIN vs. visual TIN (task section 2, read this first) ──────
//
// The MEASUREMENT TIN is the full, exact triangulation computed by
// volumeWorker.js and is what `result.positive/negative/net/coveredArea/
// surfaceArea` etc. are derived from. It is NEVER touched here.
//
// The VISUAL TIN (`vizSupported`/`vizUnsupported`) is a separate,
// rendering-only artifact - the FULL supported/unsupported triangle set
// (task section 9-11: an earlier stride-based cap fragmented mesh topology
// and is no longer used), local-frame (u,v,w), sent by the Worker. Nothing
// built here is ever fed back into a measurement; toggling visibility only
// ever flips `Object3D.visible` (see `setVolumeQaVisibility`).
//
// ── ROOT CAUSE of the reported fragmented/jittery TIN (confirmed, not
// assumed - see this iteration's own numeric Float32-ULP test) ───────────
//
// The PREVIOUS version of this file converted every vertex from local
// (u,v,w) to WORLD coordinates (`origin + u*uAxis + v*vAxis + w*normal`)
// and stored the result directly in a `Float32BufferAttribute`. At this
// project's real UTM coordinate magnitudes (Y ~ 6,256,770), 32-bit float
// has an ULP of roughly 0.25 m - HALF A METRE. Any two vertices whose real-
// world positions differ by less than that collapse to the IDENTICAL
// stored Float32 value; at the X magnitude (~524,077) the ULP is ~1.6 cm,
// still far coarser than the cm-scale surface detail a real excavation TIN
// needs to represent. That is what looked like "flickering, fragmented,
// deformed triangles" - it was never really about materials/blending (the
// earlier opacity/depth fix was real and still correct, but not the root
// cause of vertex-level jitter).
//
// The fix (standard three.js/Potree practice for large world coordinates -
// see e.g. how point-cloud nodes and Potree's own Measure/Volume helpers
// keep vertex buffers local and put the large translation on the
// Object3D/model matrix instead): vertex buffers here NEVER contain a UTM
// coordinate. Geometry is built directly from the WORKER'S OWN local
// (u,v,w) - typically single-digit metres from the reference-plane origin,
// where Float32 ULP is on the order of 1e-7 m - and the large origin
// translation plus the u/v/normal basis rotation live entirely in this
// group's own `matrix`/`matrixWorld`, which three.js evaluates in double
// precision on the CPU (only the final GPU multiply is lower precision,
// relative to the camera, exactly like how Potree renders its own,
// similarly large-coordinate, point-cloud nodes).

const COLOR_POSITIVE = 0xff8800; // fill/påfyldning
const COLOR_NEGATIVE = 0x2f8fd6; // cut/udgravning - matches FeatureVisual's own COLOR_DEFAULT blue
const COLOR_UNSUPPORTED = 0x8a8f96; // muted, desaturated - deliberately NOT a busy translucent grey mass

// Default visibility: the cut/fill surface is the primary product and is
// on; everything else is off until the user explicitly asks for it.
export const DEFAULT_QA_VISIBILITY = {surface: true, unsupported: false, referencePlane: false, wireframe: false};

// Same sign epsilon convention as signedVolume.js's own SIGN_EPS_DEFAULT.
const SIGN_EPS = 1e-9;

function classifySign(w){
	if(w > SIGN_EPS) return 1;
	if(w < -SIGN_EPS) return -1;
	return 0;
}

// Sutherland-Hodgman clip of a triangle to one half-space of w=0 - a VISUAL
// mirror of signedVolume.js's own (unexported, LOCKED-core) `clipHalfSpace`/
// `integrateConvexPolygon`, replicated here (not imported - that file is
// not modified) so a mixed-sign triangle renders split at EXACTLY the same
// w=0 line the measurement integrates, instead of being painted as one
// solid colour by its average height (task section 12).
function clipHalfSpace(poly, keepPositive){
	const inside = (p) => (keepPositive ? p.w >= 0 : p.w <= 0);
	const out = [];
	const n = poly.length;
	for(let i = 0; i < n; i++){
		const curr = poly[i], next = poly[(i + 1) % n];
		const currIn = inside(curr), nextIn = inside(next);
		if(currIn) out.push(curr);
		if(currIn !== nextIn){
			const t = curr.w / (curr.w - next.w);
			out.push({u: curr.u + t * (next.u - curr.u), v: curr.v + t * (next.v - curr.v), w: 0});
		}
	}
	return out;
}

function fanTriangulate(poly){
	const tris = [];
	for(let i = 1; i < poly.length - 1; i++) tris.push([poly[0], poly[i], poly[i + 1]]);
	return tris;
}

// Splits one {u,v,w} triangle into positive-side and negative-side sub-
// triangles (arrays of [p0,p1,p2]), exactly at w=0 for a genuinely mixed
// triangle - visual-only, see file header.
export function splitTriangleBySign(p0, p1, p2){
	const s0 = classifySign(p0.w), s1 = classifySign(p1.w), s2 = classifySign(p2.w);
	const hasPositive = s0 > 0 || s1 > 0 || s2 > 0;
	const hasNegative = s0 < 0 || s1 < 0 || s2 < 0;
	if(!hasNegative) return {positive: [[p0, p1, p2]], negative: []};
	if(!hasPositive) return {positive: [], negative: [[p0, p1, p2]]};
	const poly = [p0, p1, p2];
	return {positive: fanTriangulate(clipHalfSpace(poly, true)), negative: fanTriangulate(clipHalfSpace(poly, false))};
}

// Reads a flat Float32Array of local (u,v,w) triangles (Worker wire format)
// and splits every triangle by sign, returning {positiveTriangles,
// negativeTriangles} as arrays of [{u,v,w},{u,v,w},{u,v,w}].
function splitAllBySign(flat32){
	const positiveTriangles = [], negativeTriangles = [];
	for(let i = 0; i < flat32.length; i += 9){
		const p0 = {u: flat32[i], v: flat32[i + 1], w: flat32[i + 2]};
		const p1 = {u: flat32[i + 3], v: flat32[i + 4], w: flat32[i + 5]};
		const p2 = {u: flat32[i + 6], v: flat32[i + 7], w: flat32[i + 8]};
		const {positive, negative} = splitTriangleBySign(p0, p1, p2);
		positiveTriangles.push(...positive);
		negativeTriangles.push(...negative);
	}
	return {positiveTriangles, negativeTriangles};
}

// Builds a BufferGeometry DIRECTLY from local (u,v,w) triangles - NO frame
// transform applied here (task section 5: "BufferGeometry position
// attribute skal indeholde (u,v,w) direkte"). Returns {geometry, maxAbs}
// (`maxAbs` = largest |component| seen, for the debug-metrics readout).
function trianglesToLocalGeometry(triangles){
	const positions = new Float32Array(triangles.length * 9);
	let maxAbs = 0;
	for(let i = 0; i < triangles.length; i++){
		const [p0, p1, p2] = triangles[i];
		const base = i * 9;
		positions[base] = p0.u; positions[base + 1] = p0.v; positions[base + 2] = p0.w;
		positions[base + 3] = p1.u; positions[base + 4] = p1.v; positions[base + 5] = p1.w;
		positions[base + 6] = p2.u; positions[base + 7] = p2.v; positions[base + 8] = p2.w;
	}
	for(let i = 0; i < positions.length; i++) maxAbs = Math.max(maxAbs, Math.abs(positions[i]));
	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
	geometry.computeVertexNormals();
	geometry.computeBoundingBox();
	geometry.computeBoundingSphere();
	return {geometry, maxAbs};
}

function flat32ToLocalGeometry(flat32){
	const n = flat32.length / 3;
	const positions = new Float32Array(flat32.length);
	positions.set(flat32);
	let maxAbs = 0;
	for(let i = 0; i < positions.length; i++) maxAbs = Math.max(maxAbs, Math.abs(positions[i]));
	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
	geometry.computeVertexNormals();
	geometry.computeBoundingBox();
	geometry.computeBoundingSphere();
	return {geometry, maxAbs, triangleCount: n};
}

// Semi-transparent, double-sided cut/fill surface (task section 3-5 of the
// visibility/transparency iteration). Two requirements that used to be in
// tension - "visible from both sides while orbiting a pit" and "no
// flicker" - turn out NOT to be in tension once the vertex-precision root
// cause (see file header) is fixed: with real local-frame geometry
// (nothing collapsing/jittering at the vertex level any more), the
// SIMPLEST stable combination - transparent + DoubleSide + depthWrite:true
// + depthTest:true - was tested live and held up under orbit/zoom with no
// flicker. Kept deliberately simple: no shaders, no alpha hashing, no
// order-independent-transparency - none of that was needed once the real
// root cause (Float32/UTM precision) was fixed.
export const SURFACE_OPACITY = 0.45; // task section 4: "moderat værdi", chosen within the requested 0.35-0.50 range
function makeSurfaceMesh(geometry, color){
	const material = new THREE.MeshBasicMaterial({
		color, side: THREE.DoubleSide, transparent: true, opacity: SURFACE_OPACITY,
		depthWrite: true, depthTest: true,
	});
	return new THREE.Mesh(geometry, material);
}

function makeUnsupportedMesh(geometry){
	const material = new THREE.MeshBasicMaterial({color: COLOR_UNSUPPORTED, side: THREE.FrontSide, transparent: false, depthWrite: true, depthTest: true});
	const mesh = new THREE.Mesh(geometry, material);
	mesh.renderOrder = 1;
	return mesh;
}

// Local (u,v,w) flat quad at w=0, sized to the polygon's own (u,v) bounding
// box - task section 8: built in the SAME local frame as the surface, as a
// child of the same frame-transformed group, so no separate world-space
// math is needed at all; alignment between TIN/reference-plane/wireframe is
// then exact by construction (they share one parent transform).
function makeReferencePlaneMesh(minU, maxU, minV, maxV){
	const width = Math.max(1e-6, maxU - minU), height = Math.max(1e-6, maxV - minV);
	const geometry = new THREE.PlaneGeometry(width, height);
	const material = new THREE.MeshBasicMaterial({
		color: 0xffffff, transparent: true, opacity: 0.12, side: THREE.DoubleSide,
		depthWrite: false, depthTest: true,
		polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1,
	});
	const mesh = new THREE.Mesh(geometry, material);
	mesh.renderOrder = 2;
	mesh.position.set((minU + maxU) / 2, (minV + maxV) / 2, 0); // local - no basis/origin math needed here
	return mesh;
}

function makeWireframe(geometry){
	const edges = new THREE.EdgesGeometry(geometry);
	const material = new THREE.LineBasicMaterial({color: 0x1a1a1a, transparent: true, opacity: 0.55, depthTest: true});
	const lines = new THREE.LineSegments(edges, material);
	lines.renderOrder = 1;
	return lines;
}

// Walks up to the root ancestor and updates the WHOLE chain's matrixWorld -
// task section 7: never assume `parent.matrixWorld` is already current
// (Potree's own render loop keeps it current in practice, but this makes
// the QA group's transform correct regardless of when it is built/called).
function updateAncestryMatrixWorld(object){
	let root = object;
	while(root.parent) root = root.parent;
	root.updateMatrixWorld(true);
}

function computeFrameMatrix(frame){
	const m = new THREE.Matrix4().makeBasis(
		new THREE.Vector3(frame.uAxis.x, frame.uAxis.y, frame.uAxis.z),
		new THREE.Vector3(frame.vAxis.x, frame.vAxis.y, frame.vAxis.z),
		new THREE.Vector3(frame.normal.x, frame.normal.y, frame.normal.z),
	);
	m.setPosition(frame.origin.x, frame.origin.y, frame.origin.z);
	return m;
}

// Sets `group`'s own LOCAL `.matrix` (relative to its current parent, which
// may already be in the scene graph) so that `group.matrixWorld` ends up
// EXACTLY equal to the desired world-space frame transform (task section
// 6/7): M = [uAxis vAxis normal | origin]. `parent` may be null (group has
// no parent yet, or is meant to sit directly under an identity root).
export function applyLocalFrameTransform(group, frame, parent){
	const desiredWorld = computeFrameMatrix(frame);
	if(parent){
		updateAncestryMatrixWorld(parent);
		const parentInverse = new THREE.Matrix4().copy(parent.matrixWorld).invert();
		group.matrix.multiplyMatrices(parentInverse, desiredWorld);
	}else{
		group.matrix.copy(desiredWorld);
	}
	group.matrix.decompose(group.position, group.quaternion, group.scale);
	group.matrixWorldNeedsUpdate = true;
}

// Applies a visibility state (see DEFAULT_QA_VISIBILITY) to an already-built
// QA group. Pure Object3D.visible flips - never touches geometry/result.
export function setVolumeQaVisibility(group, visibility){
	const surface = group.getObjectByName("qa-surface");
	const unsupported = group.getObjectByName("qa-unsupported");
	const referencePlane = group.getObjectByName("qa-reference-plane");
	const wireframe = group.getObjectByName("qa-wireframe");
	if(surface) surface.visible = !!visibility.surface;
	if(unsupported) unsupported.visible = !!visibility.unsupported;
	if(referencePlane) referencePlane.visible = !!visibility.referencePlane;
	if(wireframe) wireframe.visible = !!visibility.wireframe;
}

// Returns {group, meshCount, visualTriangleCount, buildTimeMs, debug:
// {coordinateMode, maxAbsLocalPosition, frameOrigin}}. `parent`: the
// Object3D this group is about to be (or already has been) added to - used
// only to compute the correct local `matrix` (task section 6/7); the
// caller still performs the actual `parent.add(group)`.
export function buildVolumeQaVisual(result, vizSupported, vizUnsupported, boundaryLocal, initialVisibility = DEFAULT_QA_VISIBILITY, parent = null){
	const t0 = performance.now();
	const group = new THREE.Group();
	group.name = "volume-qa";
	const frame = result.plane;
	let meshCount = 0;
	let maxAbsLocalPosition = 0;
	const visualTriangleCount = (vizSupported.length + vizUnsupported.length) / 9;

	applyLocalFrameTransform(group, frame, parent);

	// task section 12: split by EXACT w=0 sign, not average-height - a
	// mixed triangle now renders as two correctly-coloured pieces, matching
	// what signedVolume.js actually integrates.
	const {positiveTriangles, negativeTriangles} = splitAllBySign(vizSupported);

	const surfaceGroup = new THREE.Group();
	surfaceGroup.name = "qa-surface";
	surfaceGroup.visible = !!initialVisibility.surface;
	if(positiveTriangles.length > 0){
		const {geometry, maxAbs} = trianglesToLocalGeometry(positiveTriangles);
		surfaceGroup.add(makeSurfaceMesh(geometry, COLOR_POSITIVE));
		maxAbsLocalPosition = Math.max(maxAbsLocalPosition, maxAbs);
		meshCount++;
	}
	if(negativeTriangles.length > 0){
		const {geometry, maxAbs} = trianglesToLocalGeometry(negativeTriangles);
		surfaceGroup.add(makeSurfaceMesh(geometry, COLOR_NEGATIVE));
		maxAbsLocalPosition = Math.max(maxAbsLocalPosition, maxAbs);
		meshCount++;
	}
	group.add(surfaceGroup);

	if(vizUnsupported.length > 0){
		const {geometry, maxAbs} = flat32ToLocalGeometry(vizUnsupported);
		const unsupportedMesh = makeUnsupportedMesh(geometry);
		unsupportedMesh.name = "qa-unsupported";
		unsupportedMesh.visible = !!initialVisibility.unsupported;
		group.add(unsupportedMesh);
		maxAbsLocalPosition = Math.max(maxAbsLocalPosition, maxAbs);
		meshCount++;
	}

	let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
	for(const p of boundaryLocal){
		if(p.u < minU) minU = p.u; if(p.u > maxU) maxU = p.u;
		if(p.v < minV) minV = p.v; if(p.v > maxV) maxV = p.v;
	}
	const referencePlaneMesh = makeReferencePlaneMesh(minU, maxU, minV, maxV);
	referencePlaneMesh.name = "qa-reference-plane";
	referencePlaneMesh.visible = !!initialVisibility.referencePlane;
	group.add(referencePlaneMesh);
	meshCount++;

	if(vizSupported.length > 0){
		const {geometry} = flat32ToLocalGeometry(vizSupported);
		const wireframe = makeWireframe(geometry);
		wireframe.name = "qa-wireframe";
		wireframe.visible = !!initialVisibility.wireframe;
		group.add(wireframe);
		meshCount++;
	}

	return {
		group,
		meshCount,
		visualTriangleCount,
		buildTimeMs: performance.now() - t0,
		debug: {
			coordinateMode: "LOCAL_FRAME",
			maxAbsLocalPosition,
			frameOrigin: {x: frame.origin.x, y: frame.origin.y, z: frame.origin.z},
		},
	};
}
